import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import StatusBadge from "@/components/feature/StatusBadge";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { topics } from "@/mocks/topics";
import { categories, siteMeta } from "@/mocks/site";
import ArticleTimeline from "./components/ArticleTimeline";
import EvidencePanel from "./components/EvidencePanel";
import RelatedTopics from "./components/RelatedTopics";

const origin = "https://youhuochigua.example";

const tocItems = [
  { id: "conclusion", label: "关键结论" },
  { id: "timeline", label: "完整时间线" },
  { id: "evidence", label: "证据清单" },
  { id: "qa", label: "本篇问答" },
];

export default function TopicDetail() {
  const { id } = useParams();
  const topic = topics.find((item) => item.id === id);

  if (!topic) {
    return (
      <>
        <Seo
          title="事件未找到 - 诱惑吃瓜"
          description="你访问的事件复盘不存在或已下线，可以返回事件复盘列表继续浏览其它事件。"
          path="/topics"
        />
        <SiteHeader />
        <main className="flex min-h-[70vh] items-center justify-center bg-background-100 px-4 py-32">
          <div className="max-w-md text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-600">
              <i className="ri-emotion-sad-line" aria-hidden="true"></i>
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
              这个事件暂时找不到
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              它可能已经合并到其它复盘，或者链接出现了小问题。回到列表页可以继续浏览全部事件。
            </p>
            <Link
              to="/topics"
              className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              返回事件复盘列表
            </Link>
          </div>
        </main>
        <SiteFooter />
      </>
    );
  }

  const categoryLabel =
    categories.find((item) => item.key === topic.category)?.label ?? topic.category;
  const related = topic.relatedIds
    .map((relatedId) => topics.find((item) => item.id === relatedId))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: topic.title,
      description: topic.summary,
      image: topic.cover,
      datePublished: topic.date,
      dateModified: topic.date,
      articleSection: topic.category,
      keywords: topic.tags.join(","),
      inLanguage: "zh-CN",
      author: { "@type": "Organization", name: siteMeta.name },
      publisher: { "@type": "Organization", name: siteMeta.name },
      mainEntityOfPage: `${origin}/topics/${topic.id}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: topic.faqs.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
  ];

  return (
    <>
      <Seo
        title={`${topic.title} - 诱惑吃瓜事件复盘`}
        description={`${topic.summary}（结论：${topic.status}｜${topic.category}）`}
        keywords={`诱惑吃瓜,${topic.tags.join(",")},事件复盘,来龙去脉`}
        path={`/topics/${topic.id}`}
        image={topic.cover}
        type="article"
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main className="bg-background-100">
        <header className="border-b border-background-200 bg-background-50 px-4 pb-8 pt-28 md:px-8 md:pb-10 md:pt-32 lg:px-12">
          <div className="mx-auto max-w-6xl">
            <Breadcrumbs
              items={[
                { label: "首页", path: "/" },
                { label: "事件复盘", path: "/topics" },
                { label: topic.title },
              ]}
            />

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
                {categoryLabel}
              </span>
              <StatusBadge status={topic.status} size="md" />
              <span className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-fire-line" aria-hidden="true"></i>
                </span>
                热度 {topic.heat.toLocaleString()}
              </span>
            </div>

            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-bold leading-tight tracking-tight text-foreground-950 md:text-4xl">
              {topic.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 md:text-sm">
              <span className="flex items-center gap-1.5">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-calendar-line" aria-hidden="true"></i>
                </span>
                整理于 {topic.date}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-time-line" aria-hidden="true"></i>
                </span>
                阅读约 {topic.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-list-check-2" aria-hidden="true"></i>
                </span>
                {topic.timeline.length} 个时间节点 · {topic.evidence.length} 份材料
              </span>
            </div>
          </div>
        </header>

        <div className="px-4 py-10 md:px-8 md:py-14 lg:px-12">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 lg:grid-cols-12">
            <article className="lg:col-span-8">
              <div className="h-60 w-full overflow-hidden rounded-lg bg-background-200 md:h-96">
                <img
                  src={topic.cover}
                  alt={`${topic.title}配图`}
                  title={`${topic.title} - 诱惑吃瓜事件复盘`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <p className="mt-6 border-l-4 border-primary-400 pl-4 text-base leading-relaxed text-foreground-800 md:text-lg">
                {topic.summary}
              </p>

              <section id="conclusion" className="mt-10 scroll-mt-24">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  关键结论
                </h2>
                <ul className="mt-5 space-y-3">
                  {topic.keyPoints.map((point, index) => (
                    <li
                      key={point}
                      className="flex gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">
                        {index + 1}
                      </span>
                      <span className="text-sm leading-relaxed text-foreground-700">
                        {point}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-5 rounded-lg border border-accent-200 bg-accent-50 p-5">
                  <p className="flex items-center gap-2 text-xs font-bold tracking-wide text-accent-800">
                    <span className="flex h-5 w-5 items-center justify-center">
                      <i className="ri-alert-line" aria-hidden="true"></i>
                    </span>
                    我们的结论倾向
                  </p>
                  <p className="mt-2.5 text-sm leading-relaxed text-foreground-700">
                    {topic.conclusion}
                  </p>
                </div>
              </section>

              <section id="timeline" className="mt-12 scroll-mt-24">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  完整时间线
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  按时间顺序排列的公开信息节点，右侧标签标明该节点的信息来源性质。
                </p>
                <div className="mt-6">
                  <ArticleTimeline items={topic.timeline} />
                </div>
              </section>

              <section id="evidence" className="mt-12 scroll-mt-24">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  证据清单
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  本篇结论所依据的材料汇总，每份材料都标注了它的证明力边界。
                </p>
                <div className="mt-6">
                  <EvidencePanel items={topic.evidence} />
                </div>
              </section>

              <section id="qa" className="mt-12 scroll-mt-24">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  本篇问答
                </h2>
                <div className="mt-5">
                  <FaqAccordion items={topic.faqs} />
                </div>
              </section>

              <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-4 text-xs leading-relaxed text-foreground-500">
                <p className="flex items-center gap-2 font-medium text-foreground-700">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-edit-2-line" aria-hidden="true"></i>
                  </span>
                  修订说明
                </p>
                <p className="mt-2">
                  本文最近一次整理时间为 {topic.date}。如后续出现新的可核实材料，我们会在时间线中插入对应节点，并在本处标注修订时间与修订原因，旧结论保留以便对照。
                </p>
              </div>

              <RelatedTopics items={related} />
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                <nav
                  aria-label="本篇目录"
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                    <span className="flex h-4 w-4 items-center justify-center text-primary-500">
                      <i className="ri-list-unordered" aria-hidden="true"></i>
                    </span>
                    本篇目录
                  </h2>
                  <ul className="mt-4 space-y-1">
                    {tocItems.map((item) => (
                      <li key={item.id}>
                        <a
                          href={`#${item.id}`}
                          className="block cursor-pointer rounded-md px-3 py-2 text-sm text-foreground-600 transition-colors hover:bg-background-100 hover:text-primary-600"
                        >
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>

                <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                    <span className="flex h-4 w-4 items-center justify-center text-primary-500">
                      <i className="ri-information-line" aria-hidden="true"></i>
                    </span>
                    结论状态说明
                  </h2>
                  <dl className="mt-4 space-y-3 text-xs leading-relaxed">
                    <div className="flex items-start gap-2.5">
                      <dt className="shrink-0 pt-0.5">
                        <StatusBadge status="实锤" />
                      </dt>
                      <dd className="text-foreground-600">关键事实有可验证材料支撑</dd>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <dt className="shrink-0 pt-0.5">
                        <StatusBadge status="反转" />
                      </dt>
                      <dd className="text-foreground-600">后续信息推翻了此前的普遍说法</dd>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <dt className="shrink-0 pt-0.5">
                        <StatusBadge status="辟谣" />
                      </dt>
                      <dd className="text-foreground-600">传言经核对后不成立</dd>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <dt className="shrink-0 pt-0.5">
                        <StatusBadge status="待跟进" />
                      </dt>
                      <dd className="text-foreground-600">材料不足以定论，持续更新中</dd>
                    </div>
                  </dl>
                </div>

                <div className="mt-4 rounded-lg bg-primary-600 p-5">
                  <p className="text-sm font-medium text-background-50">
                    有新的材料线索？
                  </p>
                  <p className="mt-2 text-xs leading-relaxed text-primary-100">
                    把你看到的一手信息发给我们，核实后会让这篇复盘更完整。
                  </p>
                  <Link
                    to="/contact"
                    className="mt-4 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-background-50 px-4 py-2 text-xs font-medium text-primary-700 transition-colors hover:bg-accent-100"
                  >
                    提交材料线索
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}